import { useState, useEffect } from "react";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";

const SocialProofSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      quote:
        "NationCard™ has revolutionized how we understand public sentiment. The real-time data helped us adjust our healthcare policies based on actual citizen feedback.",
      name: "Dr. Sarah Chen",
      role: "Director of Public Policy",
      organization: "Ministry of Health, Singapore",
      avatar: "SC",
    },
    {
      quote:
        "The verified voter panels gave us unprecedented insight into voter behavior. We could track sentiment shifts in real-time and adjust our campaign strategy accordingly.",
      name: "Michael Rodriguez",
      role: "Campaign Manager",
      organization: "Progressive Alliance Campaign",
      avatar: "MR",
    },
    {
      quote:
        "As a research institution, data integrity is crucial. NationCard™'s verification system ensures our studies are based on authentic, representative populations.",
      name: "Prof. James Wilson",
      role: "Senior Research Fellow",
      organization: "Policy Institute",
      avatar: "JW",
    },
    {
      quote:
        "The instant alerts on brand sentiment spikes have saved us from multiple PR crises. We can now respond to public concerns within hours, not days.",
      name: "Lisa Park",
      role: "Global Brand Director",
      organization: "Technology Company",
      avatar: "LP",
    },
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  return (
    <section className="py-20 bg-white dark:bg-gray-800">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Trusted By Logos */}
          <div className="text-center mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
              Trusted by Ministries, Campaigns, NGOs, Corps, Universities
            </h2>

            {/* Logo placeholders - in a real implementation, these would be actual logos */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center">
              <div className="bg-gray-300 dark:bg-gray-600 h-12 rounded-lg flex items-center justify-center border border-gray-400 dark:border-gray-500">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                  Ministry of Health
                </span>
              </div>
              <div className="bg-gray-300 dark:bg-gray-600 h-12 rounded-lg flex items-center justify-center border border-gray-400 dark:border-gray-500">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                  Universities
                </span>
              </div>
              <div className="bg-gray-300 dark:bg-gray-600 h-12 rounded-lg flex items-center justify-center border border-gray-400 dark:border-gray-500">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                  Global NGO Alliance
                </span>
              </div>
              <div className="bg-gray-300 dark:bg-gray-600 h-12 rounded-lg flex items-center justify-center border border-gray-400 dark:border-gray-500">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                  Corporations
                </span>
              </div>
              <div className="bg-gray-300 dark:bg-gray-600 h-12 rounded-lg flex items-center justify-center border border-gray-400 dark:border-gray-500">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                  Campaign Alliance
                </span>
              </div>
            </div>
          </div>

          {/* Testimonial Carousel */}
          <div className="relative bg-gradient-to-br from-nation-blue via-nation-blue-dark to-nation-purple rounded-3xl p-12 text-white">
            <div className="absolute inset-0 bg-black/20 rounded-3xl"></div>

            <div className="relative z-10">
              {/* Quote Icon */}
              <div className="flex justify-center mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-full p-4">
                  <Quote className="h-8 w-8 text-nation-teal" />
                </div>
              </div>

              {/* Testimonial Content */}
              <div className="text-center max-w-4xl mx-auto">
                <blockquote className="text-xl md:text-2xl font-medium mb-8 leading-relaxed italic">
                  "{testimonials[currentTestimonial].quote}"
                </blockquote>

                <div className="flex items-center justify-center gap-4">
                  {/* Avatar */}
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center font-bold text-lg">
                    {testimonials[currentTestimonial].avatar}
                  </div>

                  {/* Author Info */}
                  <div className="text-left">
                    <div className="font-bold text-lg">
                      {testimonials[currentTestimonial].name}
                    </div>
                    <div className="text-white/80">
                      {testimonials[currentTestimonial].role}
                    </div>
                    <div className="text-nation-teal font-semibold">
                      {testimonials[currentTestimonial].organization}
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-center items-center gap-6 mt-8">
                <button
                  onClick={prevTestimonial}
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-3 transition-all duration-300"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>

                {/* Dots indicator */}
                <div className="flex gap-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentTestimonial(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentTestimonial
                          ? "bg-nation-teal"
                          : "bg-white/30 hover:bg-white/50"
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextTestimonial}
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-3 transition-all duration-300"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-8">
              <div className="text-4xl font-bold text-nation-blue mb-2">
                500+
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                Organizations Trust Us
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-8">
              <div className="text-4xl font-bold text-nation-teal mb-2">
                95%
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                Client Satisfaction
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-8">
              <div className="text-4xl font-bold text-nation-purple mb-2">
                24/7
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                Expert Support
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialProofSection;
